import { FC, useEffect, useMemo, useRef} from "react";
import { SwitchItem, SwitcherWrapper } from "./styles";
import { ArrowCornerSvg } from "shared/assets/16/ArrowCornerSvg";
import { Colors, Skeleton, useBreakpoint } from "@brg/ui";
import { useDispatch, useSelector } from "react-redux";
import TagPageSlice, { TagPageSelector, tagPageCategories as TagPageSliceCategories } from "shared/reducers/TagPageSlice";
import { useTranslation } from "react-i18next";
import { ProjectsCount } from "./projectsCount";
import styled, { createGlobalStyle, useTheme } from "styled-components";
import { LayoutConst } from "core";
import { DropdownTabs } from "shared/pages/exploreHseContextsNew/components/DropdownTabs";
import { AppTester } from "supporting";
import { InfinityProjectsSelector } from "shared/reducers/InfinityProjectsNew";
import { useParams } from "react-router-dom";
import useQueryState from "shared/hooks/useQueryState";
import { DropdownWithExternalsLinks } from "shared/navbars/components/contextTabsItems";
import useAppTester from "shared/MobileMenu/hooks/useAppTester";

const NULLABLE_COUNT = 0;


const Wrapper = styled.div`
    place-items: center;
    display: flex;
    flex-flow: row;
    gap: 24px;
`;

const MobileDropdownStyles = createGlobalStyle`
    .tag-page-switcher-wrapper .dropdown-button {
        height: 24px;
    }

    .target-icon-wrapper {
        height: 100%;
    }
`;

export enum tagPageCategories {
    bestProjects = 'bestProjects',
    portfolio = 'portfolio',
    education = 'education',
    competitions = 'competitions',
    experts = 'experts',
    organizations = 'organizations',
    reproductions = 'reproductions'
}

export const TagPageSwitcher: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>(); 
    const { 
        currentCategory, 
        categoryProjectsCount,
        counts, 
        isModerOrExpert,
        isFetching: isTabsFetching
    } = useSelector(TagPageSelector);
    const theme = useTheme();
    const isDarkThemeDropdown = !(theme as { isDarkText?: boolean }).isDarkText;
    const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const is1280 = useBreakpoint(1280);
    const is992 = useBreakpoint(992);
    const isTab = useBreakpoint(1199)
    
    const {
        isFr,
        isAgeytomesh,
        isAcademy
    } = useAppTester();

    const [{ approved, notApproved, notModerated, temp }] = useQueryState();

    const isPreloadLoading = useSelector(InfinityProjectsSelector)?.isTabsLoading;
    const { tagIds } = useSelector(InfinityProjectsSelector)?.filter;

    const isCurrentTagId = useMemo(() => tagIds && tagIds?.includes(id) , [tagIds, id])

    const reproductionsAllowedTagIds = [
        'b0b2ee60d6654ed7958c76af78f36b56',
        'f8ae706cc07c4765849adce280bca985',
        '6a671d4e845048ef9bea62b9ef8b51bf',
        '2f1b2df5bd55430c96b8fea720c0a732',
        '1d3a314d1b594614a5d4ab55c93e68f0',
        'c49ad6f9251e47c886154fefe635f56d'
    ];

    const shouldShowReproductions = useMemo(() => {
        return id && reproductionsAllowedTagIds.includes(id);
    }, [id]);

    const onPageChange = (s: tagPageCategories) => {
        if (s === tagPageCategories.reproductions) {
            const baseUrl = id === '2f1b2df5bd55430c96b8fea720c0a732' 
                ? process.env.REACT_APP_URL_RUSSIAN_GALLERY 
                : process.env.REACT_APP_GALLERY_LINK;
        
            const reproductionsUrl = `${baseUrl}/tc/${id}`;
            
            window.open(reproductionsUrl, '_blank'); // открываем нужный tc/id
            return;
        }        
        
        dispatch(TagPageSlice.actions.setCategoryProjectsCount(0));
        dispatch(TagPageSlice.actions.setCurrentPage(s as TagPageSliceCategories));
    };

    const hasParams = useMemo(() => {
        return approved || notApproved || notModerated || temp
    } , [notApproved, notModerated, approved, temp])

    const activeCategory = useMemo(() => {
        if (hasParams && isModerOrExpert && currentCategory === tagPageCategories.bestProjects) {
            return tagPageCategories.portfolio
        }
        return currentCategory
    } , [isMobile, currentCategory, hasParams, isModerOrExpert, isMobile])

    const getCountString = (title: string) => {
        if (!!isPreloadLoading || !isCurrentTagId || isTabsFetching) return '';
        if (!!counts?.length) {
            const count = (counts?.find((i) => i?.title === title)?.count || '0' )
            return ` | ${count}`
        }
        return ''
    }

    const allCategories = useMemo(() => {
        return Object.values(tagPageCategories).filter((title) => {
            if (isAgeytomesh && title === tagPageCategories.competitions) return false;
            if (isModerOrExpert && hasParams && title === tagPageCategories.bestProjects) return false;
            if (!shouldShowReproductions && title === tagPageCategories.reproductions) return false;
            if (isFr && title === tagPageCategories.education) return false;
            return true;
        });
    }, [isAgeytomesh, isModerOrExpert, hasParams, shouldShowReproductions])

    let MAX_VISIBLE_TABS = 5
    if (isTab) {
    MAX_VISIBLE_TABS = 3
    }

    const visibleCategories = allCategories.slice(0, MAX_VISIBLE_TABS);
    const hiddenCategories = allCategories.slice(MAX_VISIBLE_TABS);
    const shouldDisplayDropdown = hiddenCategories.length > 0;
    const formatForDropdown = hiddenCategories.map((title) => ({
        title: (title === tagPageCategories.bestProjects ||
            title === tagPageCategories.portfolio ||
            title === tagPageCategories.organizations
            ? t(`tag_page.switcher.item.${title}`, { count: getCountString(title) })
            : t(`tag_page.switcher.item.${title}`)) as string,
        value: title,
        ctx: "",
        active: activeCategory === title,
        external: false,
        isTeam: false,
        name: title,
        action: () => onPageChange(title)
    }));

    const localizedTabs = useMemo(() => {
        const tabs: Record<string, string> = {};
        allCategories.forEach((title) => {
            if (
                title === tagPageCategories.bestProjects ||
                title === tagPageCategories.portfolio ||
                title === tagPageCategories.organizations
            ) {
                tabs[title] = t(`tag_page.switcher.item.${title}`, { count: getCountString(title) }) as string;
            } else {
                tabs[title] = t(`tag_page.switcher.item.${title}`) as string;
            }
        });
        return tabs;
    }, [allCategories, t, getCountString])


    const tabs = useMemo(() => {
        if (!isMobile) {
            return (
                <Wrapper>
                    {visibleCategories.map((title) => {
                        const countVisible = [
                            tagPageCategories.bestProjects,
                            tagPageCategories.portfolio,
                            tagPageCategories.organizations,
                        ].includes(title);
                        return (
                            <SwitchItem
                                key={title}
                                onClick={() => onPageChange(title)}
                                isActive={activeCategory === title}
                            >
                                {countVisible
                                    ? t(`tag_page.switcher.item.${title}`, { count: getCountString(title) })
                                    : t(`tag_page.switcher.item.${title}`)}
                            </SwitchItem>
                        );
                    })}
                    {shouldDisplayDropdown && (
                        <DropdownWithExternalsLinks
                            disableLink
                            isDarkTheme={isDarkThemeDropdown}
                            useCompetitionPanelColors={activeCategory === tagPageCategories.competitions}
                            maxHeight={460}
                            maxWidth={400}
                            list={formatForDropdown}
                            initialTab={''}
                            hoverOpen={false}
                        />
                    )}
                </Wrapper>
            )
        }
        return (
                  <>
                    <MobileDropdownStyles/>
                    <Wrapper className={'tag-page-switcher-wrapper'}>
                        <DropdownTabs
                        isAcademy={isAcademy}
                        customTitleTheme={theme as any}
                        whiteTheme={currentCategory === tagPageCategories.competitions}
                        activeTabKey={activeCategory}
                        mobileShrink
                        isShort={true}
                        items={localizedTabs}
                        //@ts-ignore
                        onChange={onPageChange}
                        />
                    </Wrapper>
                  </>
        )
    } , [onPageChange, hasParams, activeCategory, isModerOrExpert, categoryProjectsCount, theme, isDarkThemeDropdown, isPreloadLoading, isMobile, currentCategory, t, isAgeytomesh, shouldShowReproductions, visibleCategories, shouldDisplayDropdown, formatForDropdown, allCategories, getCountString, isAcademy])

    return (
        <SwitcherWrapper gx={24} gxMobile={24} importantBG={currentCategory === tagPageCategories.competitions}>
           {tabs}
        </SwitcherWrapper>
    )
}import { FC, useEffect, useMemo, useRef} from "react";
import { SwitchItem, SwitcherWrapper } from "./styles";
import { ArrowCornerSvg } from "shared/assets/16/ArrowCornerSvg";
import { Colors, Skeleton, useBreakpoint } from "@brg/ui";
import { useDispatch, useSelector } from "react-redux";
import TagPageSlice, { TagPageSelector, tagPageCategories as TagPageSliceCategories } from "shared/reducers/TagPageSlice";
import { useTranslation } from "react-i18next";
import { ProjectsCount } from "./projectsCount";
import styled, { createGlobalStyle, useTheme } from "styled-components";
import { LayoutConst } from "core";
import { DropdownTabs } from "shared/pages/exploreHseContextsNew/components/DropdownTabs";
import { AppTester } from "supporting";
import { InfinityProjectsSelector } from "shared/reducers/InfinityProjectsNew";
import { useParams } from "react-router-dom";
import useQueryState from "shared/hooks/useQueryState";
import { DropdownWithExternalsLinks } from "shared/navbars/components/contextTabsItems";
import useAppTester from "shared/MobileMenu/hooks/useAppTester";

const NULLABLE_COUNT = 0;


const Wrapper = styled.div`
    place-items: center;
    display: flex;
    flex-flow: row;
    gap: 24px;
`;

const MobileDropdownStyles = createGlobalStyle`
    .tag-page-switcher-wrapper .dropdown-button {
        height: 24px;
    }

    .target-icon-wrapper {
        height: 100%;
    }
`;

export enum tagPageCategories {
    bestProjects = 'bestProjects',
    portfolio = 'portfolio',
    education = 'education',
    competitions = 'competitions',
    experts = 'experts',
    organizations = 'organizations',
    reproductions = 'reproductions'
}

export const TagPageSwitcher: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>(); 
    const { 
        currentCategory, 
        categoryProjectsCount,
        counts, 
        isModerOrExpert,
        isFetching: isTabsFetching
    } = useSelector(TagPageSelector);
    const theme = useTheme();
    const isDarkThemeDropdown = !(theme as { isDarkText?: boolean }).isDarkText;
    const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const is1280 = useBreakpoint(1280);
    const is992 = useBreakpoint(992);
    const isTab = useBreakpoint(1199)
    
    const {
        isFr,
        isAgeytomesh,
        isAcademy
    } = useAppTester();

    const [{ approved, notApproved, notModerated, temp }] = useQueryState();

    const isPreloadLoading = useSelector(InfinityProjectsSelector)?.isTabsLoading;
    const { tagIds } = useSelector(InfinityProjectsSelector)?.filter;

    const isCurrentTagId = useMemo(() => tagIds && tagIds?.includes(id) , [tagIds, id])

    const reproductionsAllowedTagIds = [
        'b0b2ee60d6654ed7958c76af78f36b56',
        'f8ae706cc07c4765849adce280bca985',
        '6a671d4e845048ef9bea62b9ef8b51bf',
        '2f1b2df5bd55430c96b8fea720c0a732',
        '1d3a314d1b594614a5d4ab55c93e68f0',
        'c49ad6f9251e47c886154fefe635f56d'
    ];

    const shouldShowReproductions = useMemo(() => {
        return id && reproductionsAllowedTagIds.includes(id);
    }, [id]);

    const onPageChange = (s: tagPageCategories) => {
        if (s === tagPageCategories.reproductions) {
            const baseUrl = id === '2f1b2df5bd55430c96b8fea720c0a732' 
                ? process.env.REACT_APP_URL_RUSSIAN_GALLERY 
                : process.env.REACT_APP_GALLERY_LINK;
        
            const reproductionsUrl = `${baseUrl}/tc/${id}`;
            
            window.open(reproductionsUrl, '_blank'); // открываем нужный tc/id
            return;
        }        
        
        dispatch(TagPageSlice.actions.setCategoryProjectsCount(0));
        dispatch(TagPageSlice.actions.setCurrentPage(s as TagPageSliceCategories));
    };

    const hasParams = useMemo(() => {
        return approved || notApproved || notModerated || temp
    } , [notApproved, notModerated, approved, temp])

    const activeCategory = useMemo(() => {
        if (hasParams && isModerOrExpert && currentCategory === tagPageCategories.bestProjects) {
            return tagPageCategories.portfolio
        }
        return currentCategory
    } , [isMobile, currentCategory, hasParams, isModerOrExpert, isMobile])

    const getCountString = (title: string) => {
        if (!!isPreloadLoading || !isCurrentTagId || isTabsFetching) return '';
        if (!!counts?.length) {
            const count = (counts?.find((i) => i?.title === title)?.count || '0' )
            return ` | ${count}`
        }
        return ''
    }

    const allCategories = useMemo(() => {
        return Object.values(tagPageCategories).filter((title) => {
            if (isAgeytomesh && title === tagPageCategories.competitions) return false;
            if (isModerOrExpert && hasParams && title === tagPageCategories.bestProjects) return false;
            if (!shouldShowReproductions && title === tagPageCategories.reproductions) return false;
            if (isFr && title === tagPageCategories.education) return false;
            return true;
        });
    }, [isAgeytomesh, isModerOrExpert, hasParams, shouldShowReproductions])

    let MAX_VISIBLE_TABS = 5
    if (isTab) {
    MAX_VISIBLE_TABS = 3
    }

    const visibleCategories = allCategories.slice(0, MAX_VISIBLE_TABS);
    const hiddenCategories = allCategories.slice(MAX_VISIBLE_TABS);
    const shouldDisplayDropdown = hiddenCategories.length > 0;
    const formatForDropdown = hiddenCategories.map((title) => ({
        title: (title === tagPageCategories.bestProjects ||
            title === tagPageCategories.portfolio ||
            title === tagPageCategories.organizations
            ? t(`tag_page.switcher.item.${title}`, { count: getCountString(title) })
            : t(`tag_page.switcher.item.${title}`)) as string,
        value: title,
        ctx: "",
        active: activeCategory === title,
        external: false,
        isTeam: false,
        name: title,
        action: () => onPageChange(title)
    }));

    const localizedTabs = useMemo(() => {
        const tabs: Record<string, string> = {};
        allCategories.forEach((title) => {
            if (
                title === tagPageCategories.bestProjects ||
                title === tagPageCategories.portfolio ||
                title === tagPageCategories.organizations
            ) {
                tabs[title] = t(`tag_page.switcher.item.${title}`, { count: getCountString(title) }) as string;
            } else {
                tabs[title] = t(`tag_page.switcher.item.${title}`) as string;
            }
        });
        return tabs;
    }, [allCategories, t, getCountString])


    const tabs = useMemo(() => {
        if (!isMobile) {
            return (
                <Wrapper>
                    {visibleCategories.map((title) => {
                        const countVisible = [
                            tagPageCategories.bestProjects,
                            tagPageCategories.portfolio,
                            tagPageCategories.organizations,
                        ].includes(title);
                        return (
                            <SwitchItem
                                key={title}
                                onClick={() => onPageChange(title)}
                                isActive={activeCategory === title}
                            >
                                {countVisible
                                    ? t(`tag_page.switcher.item.${title}`, { count: getCountString(title) })
                                    : t(`tag_page.switcher.item.${title}`)}
                            </SwitchItem>
                        );
                    })}
                    {shouldDisplayDropdown && (
                        <DropdownWithExternalsLinks
                            disableLink
                            isDarkTheme={isDarkThemeDropdown}
                            useCompetitionPanelColors={activeCategory === tagPageCategories.competitions}
                            maxHeight={460}
                            maxWidth={400}
                            list={formatForDropdown}
                            initialTab={''}
                            hoverOpen={false}
                        />
                    )}
                </Wrapper>
            )
        }
        return (
                  <>
                    <MobileDropdownStyles/>
                    <Wrapper className={'tag-page-switcher-wrapper'}>
                        <DropdownTabs
                        isAcademy={isAcademy}
                        customTitleTheme={theme as any}
                        whiteTheme={currentCategory === tagPageCategories.competitions}
                        activeTabKey={activeCategory}
                        mobileShrink
                        isShort={true}
                        items={localizedTabs}
                        //@ts-ignore
                        onChange={onPageChange}
                        />
                    </Wrapper>
                  </>
        )
    } , [onPageChange, hasParams, activeCategory, isModerOrExpert, categoryProjectsCount, theme, isDarkThemeDropdown, isPreloadLoading, isMobile, currentCategory, t, isAgeytomesh, shouldShowReproductions, visibleCategories, shouldDisplayDropdown, formatForDropdown, allCategories, getCountString, isAcademy])

    return (
        <SwitcherWrapper gx={24} gxMobile={24} importantBG={currentCategory === tagPageCategories.competitions}>
           {tabs}
        </SwitcherWrapper>
    )
}